/*
 *
 * Copyright (c) 1999-2021 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {Controller} from "@luciad/ria/view/controller/Controller";
import {isHandled, EVENT_IGNORED} from "@luciad/ria/view/controller/HandleEventResult";

/**
 * Composes a chain of controllers.
 *
 * Chain different controllers together with {@link #appendController}.
 *
 * @constructor
 */
class CompositeController extends Controller {
  constructor() {
    super();
    this._delegates = [];
    this._invalidationListeners = [];
  }

  get delegates() {
    return this._delegates;
  }

  onActivate(map) {
    super.onActivate(map);
    for (const delegate of this._delegates) {
      delegate.onActivate(map);
      this._invalidationListeners.push(delegate.on("Invalidated", () => {
        this.emit("Invalidated");
      }));
    }
  };

  onDeactivate(map) {
    const superPromise = super.onDeactivate(map);
    const delegatePromises = [];
    for (const delegate of this._delegates) {
      delegatePromises.push(delegate.onDeactivate(map));
    }
    for (const invalidationListener of this._invalidationListeners) {
      invalidationListener.remove();
    }
    this._invalidationListeners = [];
    return Promise.all([superPromise].concat(delegatePromises));
  };

  onGestureEvent(gestureEvent) {
    let result = EVENT_IGNORED;
    for (const delegate of this._delegates) {
      result = delegate.onGestureEvent(gestureEvent);
      if (isHandled(result)) {
        return result;
      }
    }
    return result;
  };

  onKeyEvent(keyEvent) {
    let result = EVENT_IGNORED;
    for (const delegate of this._delegates) {
      result = delegate.onKeyEvent(keyEvent);
      if (isHandled(result)) {
        return result;
      }
    }
    return result;
  };

  onDraw(geocanvas) {
    for (const delegate of this._delegates) {
      delegate.onDraw(geocanvas);
    }
  };

  onDrawLabel(labelCanvas) {
    for (const delegate of this._delegates) {
      delegate.onDrawLabel(labelCanvas);
    }
  };

  appendController(controller) {
    if (this.map) {
      throw new Error("Cannot append new controller while current controller is active on the map." +
                      "Remove the controller from map before appending a controller to it.");
    }
    this._delegates.push(controller);
  };
}

export {CompositeController};